import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';

import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { UserRoleEnum } from 'src/graphql/schema';
import { roleOptions } from 'src/components/User';

import { useInvite } from '../model';

const formSchema = z.object({
  fullName: z.string().trim().min(1, 'Full name is required.'),
  email: z.string().trim().min(1, 'Email address is required.').email({ message: 'Invalid email format.' }),
  role: z.nativeEnum(UserRoleEnum, { message: 'Role is required' }),
});

type IAddMemberForm = z.infer<typeof formSchema>;

const memberRoleOptions = roleOptions.slice(1);

const AddMemberForm = () => {
  const { addMember, cancelAddMember } = useInvite();
  const form = useForm<IAddMemberForm>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      fullName: '',
      email: '',
      role: UserRoleEnum.User,
    },
  });

  const onSubmit = (dataForm: IAddMemberForm) =>
    addMember({ id: Date.now().toString(), name: dataForm.fullName, email: dataForm.email, role: dataForm.role });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <FormField
          control={form.control}
          name="fullName"
          render={({ field }) => (
            <FormItem className="mb-4">
              <FormLabel>Full name</FormLabel>
              <FormControl>
                <Input {...field} placeholder="e.g., Jane Smith" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem className="mb-4">
              <FormLabel>Email address</FormLabel>
              <FormControl>
                <Input {...field} placeholder="e.g., jane.smith@example.com" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="role"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="mb-4">Role</FormLabel>
              <FormControl>
                <RadioGroup onValueChange={field.onChange} defaultValue={field.value} className="flex flex-col gap-2">
                  {memberRoleOptions.map(({ label, value, description }) => (
                    <FormItem key={value} className="flex items-start gap-2 space-y-0">
                      <FormControl>
                        <RadioGroupItem value={value} />
                      </FormControl>
                      <FormLabel className="space-y-1.5">
                        <span className="block font-medium">{label}</span>
                        <p className="text-xs text-muted-foreground whitespace-pre-wrap font-normal">{description}</p>
                      </FormLabel>
                    </FormItem>
                  ))}
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="flex gap-2 mt-6">
          <Button variant="outline" onClick={cancelAddMember} type="button" size="lg" className="flex-1">
            Back
          </Button>
          <Button
            type="submit"
            isLoading={form.formState.isSubmitting}
            disabled={form.formState.isSubmitting}
            size="lg"
            className="flex-1"
          >
            Create invitation
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default AddMemberForm;
